"use client";

import { useCallback, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Which edge the drawer slides from */
  side?: "right" | "left";
  className?: string;
}

function Drawer({
  open,
  onClose,
  children,
  side = "right",
  className,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Trap focus and handle Escape
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[var(--z-modal)]"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-[var(--color-bg-overlay)] transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={cn(
          "absolute top-0 bottom-0 flex flex-col",
          "w-full max-w-md",
          "bg-[var(--color-bg-elevated)] border-[var(--color-border-default)]",
          "shadow-[var(--shadow-2xl)]",
          "transition-transform duration-[var(--duration-slow)] ease-[var(--ease-out)]",
          side === "right" && "right-0 border-l",
          side === "left" && "left-0 border-r",
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}

/* ── Subcomponents ──────────────────────────────────────────── */

function DrawerHeader({
  className,
  children,
  onClose,
}: React.ComponentProps<"div"> & { onClose?: () => void }) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-[var(--space-4)]",
        "px-[var(--space-5)] pt-[var(--space-5)] pb-[var(--space-3)]",
        "border-b border-[var(--color-border-subtle)]",
        className
      )}
    >
      <div className="flex-1 min-w-0">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="shrink-0 rounded-[var(--radius-md)] p-[var(--space-1)] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-muted)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]"
          aria-label="Close"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function DrawerBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex-1 overflow-y-auto px-[var(--space-5)] py-[var(--space-4)]",
        className
      )}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-[var(--space-3)]",
        "border-t border-[var(--color-border-subtle)]",
        "px-[var(--space-5)] py-[var(--space-4)]",
        className
      )}
      {...props}
    />
  );
}

export { Drawer, DrawerHeader, DrawerBody, DrawerFooter };
export type { DrawerProps };
